import { createFileRoute, Link } from '@tanstack/react-router';
import { FolderKanban, Plus, SearchX } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { CaseLink } from '../components/cases/in-cases';
import { DataBlock, DataNumber } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { buttonVariants } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCases } from '../lib/cases';
import { formatDate } from '../lib/library';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Cases (PLAN §2 `case`, §12.7; task 33): every investigation, with how many entities, sources and notes it groups.
// A Case is created like any entity (kind Case); its workspace files things in it and starts its chat.

export const Route = createFileRoute('/cases/')({ component: CasesPage });

const TOUR = {
  id: 'cases',
  steps: [
    {
      target: '[data-tour="cases-list"]',
      title: 'Your investigations',
      body: 'Each Case groups the entities, documents and notes of one investigation. Open one to work in it or ask the chat about it.',
      learnMore: 'cases',
    },
  ],
};

function CasesPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const canWrite = useCan('content.write');
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const searchId = useId();
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 200);
    return () => clearTimeout(t);
  }, [text]);
  const list = useCases(q);
  const cases = list.data?.cases ?? [];
  const create = canWrite ? (
    <Link to="/entity/new" search={{ kind: 'case', type: 'case' }} className={buttonVariants({ size: 'md' })}>
      <Plus aria-hidden="true" /> New Case
    </Link>
  ) : undefined;

  return (
    <>
      <PageHeader
        title="Cases"
        description="Investigations: each groups entities, Library documents and notes, and has its own chat."
        actions={create}
      />
      <Tip id="cases" learnMore="cases">
        File an entity or a document in a Case from its profile or its Library page, or from the Case itself. The chat of a Case answers
        only from what is filed there and what it links to.
      </Tip>
      <div className="mt-4 mb-3 max-w-md space-y-1">
        <label htmlFor={searchId} className="text-xs font-medium">
          Find a Case
        </label>
        <Input id={searchId} type="search" value={text} onChange={e => setText(e.target.value)} placeholder="Name or ID code" dir="auto" />
      </div>
      <div data-tour="cases-list">
        {list.isError ? (
          <ErrorState error={list.error} title="The Cases couldn’t be loaded" onRetry={() => list.refetch()} compact />
        ) : list.isPending || !snap ? (
          <div className="space-y-2" aria-busy="true">
            <span className="sr-only">Loading…</span>
            <div className="h-16 animate-pulse rounded-lg bg-muted" />
            <div className="h-16 animate-pulse rounded-lg bg-muted" />
          </div>
        ) : cases.length === 0 ? (
          q ? (
            <EmptyState icon={SearchX} title="No Case matches" learnMore="cases">
              Try another name or the Case’s ID code.
            </EmptyState>
          ) : (
            <EmptyState icon={FolderKanban} title="No Cases yet" action={create} learnMore="cases">
              A Case gathers the entities, documents and notes of one investigation, such as “Port deal”. Its chat answers only from them.
            </EmptyState>
          )
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {cases.map(({ case: c, counts, lastActivityAt }) => (
              <li key={c.id} className="space-y-2 rounded-lg border bg-surface p-4" data-testid="case-card">
                <p className="flex items-center justify-between gap-2">
                  <CaseLink schema={snap} kase={c} />
                  <span className="shrink-0 font-mono text-xs text-muted-foreground">{c.code}</span>
                </p>
                {c.summary && <DataBlock className="line-clamp-2 text-sm text-muted-foreground">{c.summary}</DataBlock>}
                <p className="text-xs text-muted-foreground">
                  <DataNumber>{counts.entities}</DataNumber> {counts.entities === 1 ? 'entity' : 'entities'} ·{' '}
                  <DataNumber>{counts.documents}</DataNumber> {counts.documents === 1 ? 'source' : 'sources'} ·{' '}
                  <DataNumber>{counts.notes}</DataNumber> {counts.notes === 1 ? 'note' : 'notes'}
                  {lastActivityAt ? ` · last change ${formatDate(lastActivityAt)}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
